import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@apollo/client';
import { showInkToast } from '../cms/InkToast';
import { dueDay, dueFull, dueWords, SAVE_CAMPAIGN_WORK, toYmd } from './campaignCards';
import { postTime, ReachPost } from './reach';

/**
 * A calendar card's work strip (campaign board, 2026-10-23): when the post
 * should be ready (2 days before it goes out unless someone set a date), a
 * note chip — hover to read, click to edit — and, on a hovered card, "+ Note"
 * and ✓ to mark it done. Nothing for a post that went out or was skipped.
 *
 * The notes open in a layer over the page, under the card and kept inside the
 * window: the week clips its days, and notes placed inside a day were cut off
 * and pushed the week sideways, leaving no way to close them.
 */

const POP_WIDTH = 250;
const GAP = 6;
const MARGIN = 8;
export function PostWork({
  post,
  hovered,
  onChanged,
}: {
  post: ReachPost;
  hovered: boolean;
  onChanged: () => void;
}) {
  const work = post.work;
  const [peek, setPeek] = useState(false);
  const [edit, setEdit] = useState<{ text: string; due: string; done: boolean } | null>(null);
  const [save, { loading }] = useMutation(SAVE_CAMPAIGN_WORK);
  const anchor = useRef<HTMLDivElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const noteBox = useRef<HTMLTextAreaElement>(null);
  const leaveTimer = useRef<number | null>(null);
  const [place, setPlace] = useState<{ left: number; top: number; above: boolean } | null>(null);
  const open = Boolean(edit || (work?.note && peek));
  // Re-placed when it switches between reading and editing (its height changes).
  const editing = edit !== null;

  // Under the card, or above it when there's no room below; never off-screen.
  const measure = useCallback(() => {
    const a = anchor.current?.getBoundingClientRect();
    if (!a) return;
    // Above means above the whole card, not over its text.
    const card = anchor.current?.closest('.rc-post')?.getBoundingClientRect() || a;
    const height = pop.current?.offsetHeight || 220;
    const left = Math.min(Math.max(MARGIN, a.left - 9), window.innerWidth - POP_WIDTH - MARGIN);
    const above =
      a.bottom + GAP + height > window.innerHeight - MARGIN && card.top - GAP - height > MARGIN;
    setPlace({ left, top: above ? card.top - GAP : a.bottom + GAP, above });
  }, []);
  useLayoutEffect(() => {
    if (open) measure();
    else setPlace(null);
  }, [open, editing, measure]);
  useEffect(() => {
    if (!open) return;
    const onMove = () => measure();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setEdit(null);
      setPeek(false);
    };
    // A click elsewhere closes it; an edit with changes stays until Cancel or Save.
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (pop.current?.contains(t) || anchor.current?.contains(t)) return;
      setPeek(false);
      setEdit((cur) =>
        cur && work && (cur.text.trim() !== (work.note || '').trim() || cur.done !== work.done)
          ? cur
          : null
      );
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, measure, work]);
  // The layer is hidden while it's placed, so autofocus can't take: focus the
  // note once it's shown.
  useEffect(() => {
    if (editing && place) noteBox.current?.focus();
  }, [editing, place]);
  useEffect(
    () => () => {
      if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
    },
    []
  );
  // Reading the notes: leaving the chip for the layer keeps it open.
  const holdPeek = () => {
    if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
  };
  const dropPeek = () => {
    holdPeek();
    leaveTimer.current = window.setTimeout(() => setPeek(false), 150);
  };

  if (!work) return null;
  const t = postTime(post);
  const d = dueDay({
    dueDate: work.dueDate,
    dueBase: t ? t.toISOString() : null,
    dueOffset: -2,
  });
  const due = dueWords(d);
  const stop = (fn: () => void) => (e: React.SyntheticEvent) => {
    e.stopPropagation();
    fn();
  };
  const run = async (vars: Record<string, unknown>, toast: string) => {
    try {
      await save({ variables: { subjectKind: 'post', subjectId: post.id, ...vars } });
      showInkToast(toast);
      setEdit(null);
      onChanged();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };
  const openEdit = () => {
    setPeek(false);
    setEdit({ text: work.note, due: d ? toYmd(d) : '', done: work.done });
  };
  return (
    <div className="rc-work" ref={anchor} onMouseLeave={dropPeek}>
      {/* It already tried to go out: whether it was ready in time means nothing now. */}
      {post.reachStatus === 'missed' ? null : work.done ? (
        <span className="cb-ready">✓ Done</span>
      ) : post.reachStatus === 'scheduled' || post.reachStatus === 'publishing' ? (
        // Scheduled: ready in time, so never overdue (backlog b35b62cf).
        <span className="cb-ready">✓ Ready</span>
      ) : d ? (
        <span className="rc-work-due" style={{ color: due.ink }}>
          {due.text}
        </span>
      ) : null}
      <span className="rc-work-tools">
        {work.note ? (
          <span
            className="cb-note-chip"
            role="button"
            tabIndex={0}
            title="Notes"
            onMouseEnter={() => {
              holdPeek();
              setPeek(true);
            }}
            onFocus={() => setPeek(true)}
            onClick={stop(openEdit)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') stop(openEdit)(e);
            }}
          >
            ✎
          </span>
        ) : hovered && !edit ? (
          <span
            className="cb-note-add"
            role="button"
            tabIndex={0}
            title="Add a note"
            onClick={stop(openEdit)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') stop(openEdit)(e);
            }}
          >
            + Note
          </span>
        ) : null}
        {hovered && !work.done && !edit ? (
          <span
            className="rc-work-done"
            role="button"
            tabIndex={0}
            title="Mark done"
            aria-label="Mark done"
            onClick={stop(() => void run({ done: true }, 'Marked done'))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') stop(() => void run({ done: true }, 'Marked done'))(e);
            }}
          >
            ✓
          </span>
        ) : null}
      </span>
      {open
        ? createPortal(
            <div
              ref={pop}
              className="cb-pop rc-work-pop"
              role="dialog"
              aria-label="Post notes"
              style={{
                left: place?.left ?? -9999,
                top: place?.top ?? 0,
                transform: place?.above ? 'translateY(-100%)' : undefined,
                visibility: place ? 'visible' : 'hidden',
              }}
              onMouseEnter={holdPeek}
              onMouseLeave={() => {
                if (!edit) dropPeek();
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <div className="cb-pop-label">NOTES</div>
              {edit ? (
                <>
                  <textarea
                    className="cb-input"
                    rows={4}
                    ref={noteBox}
                    value={edit.text}
                    placeholder="Notes"
                    onChange={(e) => setEdit({ ...edit, text: e.target.value })}
                  />
                  <label className="cb-due-field">
                    Due
                    <input
                      type="date"
                      className="cb-input"
                      value={edit.due}
                      onChange={(e) => setEdit({ ...edit, due: e.target.value })}
                    />
                  </label>
                  <div className="cb-actions" style={{ alignItems: 'center' }}>
                    <button
                      type="button"
                      className="ink-link-btn"
                      style={{
                        color: 'var(--ink-ink2)',
                        marginRight: 'auto',
                        whiteSpace: 'nowrap',
                      }}
                      onClick={() =>
                        void run(
                          { done: !work.done },
                          work.done ? 'Marked not done' : 'Marked done'
                        )
                      }
                    >
                      {work.done ? 'Mark not done' : 'Mark done'}
                    </button>
                    <button type="button" className="ink-btn" onClick={() => setEdit(null)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      disabled={loading}
                      onClick={() =>
                        void run(
                          { note: edit.text.trim(), ...(edit.due ? { dueDate: edit.due } : {}) },
                          edit.text.trim() ? 'Note saved' : 'Note removed'
                        )
                      }
                    >
                      Save
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="cb-pop-text">{work.note}</div>
                  <div className="cb-pop-foot">
                    <span>{dueFull(d)}</span>
                    <button type="button" className="ink-link-btn" onClick={stop(openEdit)}>
                      Edit
                    </button>
                  </div>
                </>
              )}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
